<template>
  <component
    :is="buttonTag ? 'button' : 'a'"
    :id="id ? id + '_btn' : null"
    v-tooltip="{ content: title, interactive: interactiveTooltip }"
    :class="buttonClass"
    :href="url ? url : buttonTag || role === 'button' ? null : '#'"
    :aria-disabled="disabled ? 'disabled' : null"
    :disabled="disabled ? 'disabled' : null"
    :role="role"
    :type="buttontype"
    tabindex="0"
    :data-button="label || hint"
    :data-label="dataLabel || label || hint || offscreenLabel"
    :aria-label="ariaLabel"
    @click="buttonClick"
    @keydown.enter="buttonClick"
    @keydown.space="onKeypressSpace"
    ><cat-icon
      v-if="icon"
      :icon="icon"
      :decorative="isIconDecorative"
      :iconLabel="isIconDecorative ? null : iconLabel"
      class="mln mrn"
    ></cat-icon
    ><slot>{{ label }}</slot>
    <cat-icon
      v-if="iconRight"
      :icon="iconRight"
      :decorative="isIconDecorative"
      :iconLabel="isIconDecorative ? null : iconLabel"
      class="mln mrn"
    ></cat-icon>
  </component>
</template>
<script lang="ts">
import { Component, Vue, Prop, Emit, Inject } from "vue-facing-decorator";
import { defaultResolveTxt } from "../field-element";
import PageElementMixin from "../field-element";
import Helpful from "@/components/utilities/helpful";
import { PropOptions } from "vue";

import CatErrors from "@/components/catErrors/catErrors.vue";
import CatIcon from "@/components/utilities/catIcon.vue";

@Component({
  name: "cat-btn",
  components: { CatErrors, CatIcon },
  mixins: [Helpful, PageElementMixin]
})
export default class CatBtn extends Vue {
  @Prop({
    default: false,
    type: Boolean,
    description: "Button styled as a primary button"
  } as PropOptions)
  public readonly primary?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Button styled as a secondary button"
  } as PropOptions)
  public readonly secondary?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Button styled as a hollow button"
  } as PropOptions)
  public readonly hollow?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Button styled with margin class"
  } as PropOptions)
  public readonly margin?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Button styled as a link"
  } as PropOptions)
  public readonly link?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description:
      "Expands horizontally to fill the available space within its parent container"
  } as PropOptions)
  public readonly fill?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description:
      "On small devices only, expands horizontally to fill the available space within its parent container"
  } as PropOptions)
  public readonly fillmobile?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Position button on right"
  } as PropOptions)
  public readonly right?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Button styled as a rounded button"
  } as PropOptions)
  public readonly rounded?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Button styled with negative class"
  } as PropOptions)
  public readonly negative?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Button styled as a disabled button"
  } as PropOptions)
  public readonly disabled?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Button with an icon only"
  } as PropOptions)
  public readonly onlyIcon?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Button styled as a small button"
  } as PropOptions)
  public readonly small?: boolean;

  @Prop({
    default: false,
    type: Boolean,
    description:
      "Splits the icon and text by adding a background colour to the icon. Can be accompanied by warning-success-danger. Must be used in conjunction with icon or iconRight."
  } as PropOptions<any>)
  public readonly split?: boolean;

  @Prop({
    default: false,
    type: Boolean,
    description: "Background colour of the icon styled as warning"
  } as PropOptions<any>)
  public readonly warning?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Background colour of the icon styled as success"
  } as PropOptions<any>)
  public readonly success?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Background colour of the icon styled as danger"
  } as PropOptions<any>)
  public readonly danger?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "Renders HTML button tag"
  } as PropOptions<any>)
  public readonly buttonTag?: boolean;
  @Prop({
    default: false,
    type: Boolean,
    description: "To make tooltips interactive"
  } as PropOptions<any>)
  public readonly interactiveTooltip?: boolean;

  @Prop({
    default: "button",
    type: String,
    description: "Aria role to apply to the button."
  } as PropOptions<any>)
  public readonly role!: string;

  @Prop({
    default: null,
    type: String,
    description: "URL to be set on the href attribute."
  } as PropOptions<any>)
  public readonly url!: string;

  @Prop({
    default: null,
    type: String,
    description: "Visual cue/Icon for the button"
  } as PropOptions<any>)
  public readonly icon?: string;
  @Prop({
    default: null,
    type: String,
    description: "Icon aligned right"
  } as PropOptions<any>)
  public readonly iconRight?: string;

  @Prop({
    type: Boolean,
    default: true,
    description:
      "Set to false to expose the icon image to the assistive technology. Otherwise, the icon is hidden from AT by default."
  } as PropOptions<any>)
  public readonly iconDecorative?: boolean;

  @Prop({
    type: String,
    description:
      "If the icon label is provided the button's icon is to be exposed to assistive technology with this accessible name."
  } as PropOptions<any>)
  public readonly iconLabel?: string;

  @Prop(String as PropOptions<string>) public readonly hint?: string;

  @Prop({
    type: String,
    description:
      "The accessible name of the button. This will be set in the button aria-label property."
  } as PropOptions<any>)
  public readonly offscreenLabel?: string;
  @Prop({
    type: Boolean,
    default: true,
    description:
      "Set to false to not display the tooltip. The hint parameter will just be set as the accessible name for button aria-label."
  } as PropOptions<any>)
  public readonly showHint?: boolean;

  @Prop({
    type: String,
    description: "Custom data attribute."
  } as PropOptions<any>)
  public readonly dataLabel?: string;

  @Prop(String as PropOptions<string>) public readonly label?: string;

  @Inject({
    default: defaultResolveTxt,
    from: "resolveTxt"
  })
  readonly resolveTxt!: Function;

  get title(): string | null | undefined {
    return this.showHint ? this.hint : null;
  }

  /**
   * The aria-label is retrieved in the following order:
   * 1. The offscreenLabel props that is passed in
   * 2. The hint text if the button has no visible label
   * 3. Otherwise, arial-label is not required
   */
  get ariaLabel(): string | null {
    return this.offscreenLabel || (!this.label ? this.hint : null) || null;
  }

  get buttonClass(): object {
    const css = {
      "cat-btn": true,
      primary: this.primary,
      secondary: this.secondary,
      hollow: this.hollow,
      rounded: this.rounded,
      small: this.small,
      margin: this.margin,
      simple:
        this.link ||
        (!this.primary &&
          !this.secondary &&
          !this.hollow &&
          !this.icon &&
          !this.iconRight &&
          !this.fill &&
          !this.fillmobile),
      button: !this.link,
      link: this.link,
      right: this.right,
      fill: this.fill,
      fillmobile: this.fillmobile,
      negative: this.negative,
      disabled: this.disabled,
      split: this.split && this.icon,
      splitright: this.split && this.iconRight,
      warning: this.warning,
      danger: this.danger,
      success: this.success,
      "only-icon": this.onlyIcon
    };
    return css;
  }

  get buttontype(): string | null {
    return this.buttonTag ? "button" : null;
  }

  get isIconDecorative(): boolean | undefined {
    return this.iconLabel && this.iconLabel !== ""
      ? false
      : this.iconDecorative;
  }

  @Emit("ui-click")
  public buttonClick(event: Event): any {
    if (this.url) {
      return false;
    }
    if (!(this.buttonTag || this.role === "button")) {
      event.preventDefault();
    }
    return {
      event,
      id: this.$attrs.id ? this.$attrs.id : null
    };
  }

  /**
   * Button supports enter and space to activate
   * Link role supports enter key only
   * @param event
   */
  onKeypressSpace(event: Event) {
    event.preventDefault();
    return this.buttonTag || this.role === "button"
      ? this.buttonClick(event)
      : false;
  }
}
</script>
